import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "@phosphor-icons/react";

export default function AddDealDialog({ repId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [customer, setCustomer] = useState("");
  const [product, setProduct] = useState("");
  const [amount, setAmount] = useState("");
  const [dealType, setDealType] = useState("setup");
  const [recurring, setRecurring] = useState(false);
  const [wonDate, setWonDate] = useState(new Date().toISOString().slice(0, 10));
  const [stage, setStage] = useState("won");
  const [collected, setCollected] = useState(false);
  const [source, setSource] = useState("stripe");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!customer || !product || !amount) { toast.error("Customer, product, amount required"); return; }
    setSaving(true);
    try {
      await api.post("/deals", {
        rep_id: repId, customer, product, sale_amount: Number(amount),
        deal_type: dealType, is_recurring: recurring, won_date: wonDate, stage,
        payment_confirmed: collected, payment_source: collected ? source : null,
      });
      toast.success("Deal recorded");
      setOpen(false); setCustomer(""); setProduct(""); setAmount(""); setCollected(false);
      onCreated && onCreated();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-deal-btn" variant="outline" size="sm" className="gap-2">
          <Plus size={15} weight="bold" /> Record Deal
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Record a Won Deal</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">Commission pays only once money is collected.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2 space-y-1.5">
            <Label className="overline">Customer</Label>
            <Input data-testid="deal-customer" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Acme Co" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline">Product</Label>
            <Input data-testid="deal-product" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="CallBoss" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline">Sale Amount ($)</Label>
            <Input data-testid="deal-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="font-mono" placeholder="297" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline">Type</Label>
            <Select value={dealType} onValueChange={setDealType}>
              <SelectTrigger data-testid="deal-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="setup">Setup fee</SelectItem>
                <SelectItem value="mrr">Monthly (MRR)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="overline">Won Date</Label>
            <Input data-testid="deal-date" type="date" value={wonDate} onChange={(e) => setWonDate(e.target.value)} className="font-mono" />
          </div>
          <div className="col-span-2 flex items-center justify-between rounded-sm border border-border p-3">
            <div className="text-sm font-medium">Recurring residual</div>
            <Switch data-testid="deal-recurring" checked={recurring} onCheckedChange={setRecurring} />
          </div>
          <div className="col-span-2 flex items-center justify-between rounded-sm border border-border p-3">
            <div>
              <div className="text-sm font-medium">Payment collected</div>
              <div className="text-xs text-muted-foreground">Commission pays only on collected money.</div>
            </div>
            <Switch data-testid="deal-collected" checked={collected} onCheckedChange={setCollected} />
          </div>
          {collected && (
            <div className="col-span-2 space-y-1.5">
              <Label className="overline">Payment Source</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="deal-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="stripe">Stripe</SelectItem>
                  <SelectItem value="whmcs">WHMCS</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="deal-submit" onClick={submit} disabled={saving}>{saving ? "Saving…" : "Record Deal"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
